import { Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useInboxStateContext from "../../../contexts/useInboxStateContext";
import type { PreviewDocument } from "../../../hooks/pdfPreview";
import { usePdfPreview } from "../../../hooks/pdfPreview";
import { useQueryParams } from "../../../hooks/useQueryParams";
import type { Message } from "../../../store/state/messages/types";
import AttachmentComponent from "./AttachmentComponent";

interface Props {
  message: Message;
  loading: boolean;
  onPreview: (data: PreviewDocument) => void;
  onDownloadAttachment: (messageId: string, attachmentId: string) => void;
}

const AttachmentsContainer = ({ message, loading, onPreview, onDownloadAttachment }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { prepareLinkForPreview } = usePdfPreview();
  const { watermarkInvestorName } = useInboxStateContext();
  const queryParams = useQueryParams();
  const attachmentId = queryParams.get("attachment");

  const previewFile = useCallback(
    (attachmentId: string) => {
      const accessOptions = message.attachments?.find((a) => a.id === attachmentId)?.accessOptions;
      prepareLinkForPreview(message.id, attachmentId, watermarkInvestorName, accessOptions)
        .then(onPreview)
        .catch(() => {
          sendNotificationError("The file cannot be previewed");
        });
    },
    [message, watermarkInvestorName, prepareLinkForPreview, onPreview, sendNotificationError]
  );

  useEffect(() => {
    if (attachmentId) {
      previewFile(attachmentId);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [attachmentId]);

  if (!message || loading || !message.attachments || message.attachments?.length <= 0) {
    return <></>;
  }

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="subtitle1">{message.attachments.length > 1 ? "Attachments" : "Attachment"}</Typography>
      {message.attachments.map((attachment) => {
        return (
          <AttachmentComponent
            key={attachment.id}
            attachment={attachment}
            onPreview={() => previewFile(attachment.id)}
            onDownload={() => onDownloadAttachment(message.id, attachment.id)}
          />
        );
      })}
    </Stack>
  );
};

export default AttachmentsContainer;
